import React, { useState } from "react";
import { Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { QuestionCircleOutlined } from "@ant-design/icons";
import {
  AdapterChangeType,
  CrawlerAdapterResultRecord,
  CrawlerProviderResultRecord,
  CrawlerRunRecord,
  HistoryResponse,
} from "../../types/crawlerHealth";
import { WizardService } from "../../services/WizardService";

interface RunHistoryTableProps {
  history: HistoryResponse | null;
  loading: boolean;
  onPageChange: (page: number, pageSize: number) => void;
  currentPage: number;
  pageSize: number;
}

const { Text } = Typography;

function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return "N/A";
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${remainingSeconds}s`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "N/A";
  return new Date(iso).toLocaleString();
}

/**
 * **Corrected 2026-09-18 (KI-39)**: `empty` used to fall through to the `default:` branch and
 * render as an unremarkable grey tag — kept below as the branch for any genuinely unknown value,
 * but it was wrong for `empty`, which is a known status carrying the whole point of KI-39 ("this
 * provider ran clean and committed nothing"). It now gets its own gold tag and a tooltip, sitting
 * deliberately between green `success` and orange `partial`: nothing failed, so it is not an
 * error, but it is not health either.
 *
 * **Corrected 2026-09-18 (KI-39 part 2)**: the `empty` gold tag above is itself wrong for a leg
 * whose `adaptersSupported === false` (currently only `bidmachine/IOS`) — that provider
 * deliberately only crawls the SDK, so zero-adapters is the expected outcome every run, not a
 * warning. Such a leg now renders a neutral "SDK only" tag instead; a leg that is not `empty`
 * (e.g. genuinely `failed`) still renders its real status even when `adaptersSupported` is
 * `false` — the override only suppresses the "found nothing" warning, not real errors.
 *
 * **Corrected 2026-09-18 (KI-39 part 2, fix)**: the condition above, `status === "empty" &&
 * adaptersSupported === false`, is kept as history and named wrong — it is unreachable. The
 * backend's `deriveProviderStatus` makes `empty` REQUIRE `adaptersSupported`, so an unsupported
 * leg with zero adapters is recorded as `success`, never `empty`. The real condition is
 * `adaptersSupported === false && (status === "success" || status === "empty")` — `empty` is kept
 * in the check for forward compatibility in case the backend rule ever changes, but in current
 * data an unsupported leg always arrives as `success`. `failed`/`partial` still render normally.
 */
function statusTag(status: string, adaptersSupported?: boolean) {
  if (adaptersSupported === false && (status === "success" || status === "empty")) {
    return (
      <Tooltip title="Network adapters are not crawled for this provider — only the SDK is tracked.">
        <Tag>SDK only</Tag>
      </Tooltip>
    );
  }
  switch (status) {
    case "success":
      return <Tag color="green">success</Tag>;
    case "empty":
      return (
        <Tooltip title="Ran without errors but committed nothing — no adapters and no changes.">
          <Tag color="gold">empty</Tag>
        </Tooltip>
      );
    case "partial":
      return <Tag color="orange">partial</Tag>;
    case "failed":
      return <Tag color="red">failed</Tag>;
    default:
      return <Tag>{status}</Tag>;
  }
}

const CHANGE_TYPE_ORDER: Record<AdapterChangeType, number> = {
  UPDATED: 0,
  CREATED: 1,
  ERROR: 2,
  SKIPPED: 3,
  UNCHANGED: 4,
};

function changeTypeTag(changeType: AdapterChangeType) {
  switch (changeType) {
    case "CREATED":
      return <Tag color="green">CREATED</Tag>;
    case "UPDATED":
      return <Tag color="blue">UPDATED</Tag>;
    case "UNCHANGED":
      return <Tag color="default">UNCHANGED</Tag>;
    case "SKIPPED":
      return <Tag color="orange">SKIPPED</Tag>;
    case "ERROR":
      return <Tag color="red">ERROR</Tag>;
    default:
      return <Tag>{changeType}</Tag>;
  }
}

const CHANGE_TYPE_FILTER_OPTIONS: { label: string; value: AdapterChangeType | "ALL" }[] = [
  { label: "All", value: "ALL" },
  { label: "Created", value: "CREATED" },
  { label: "Updated", value: "UPDATED" },
  { label: "Unchanged", value: "UNCHANGED" },
  { label: "Skipped", value: "SKIPPED" },
  { label: "Error", value: "ERROR" },
];

const adapterColumns: ColumnsType<CrawlerAdapterResultRecord> = [
  {
    title: "Network",
    dataIndex: "networkName",
    key: "networkName",
    render: (v: string) => <Text strong>{v}</Text>,
  },
  {
    title: "Adapter",
    dataIndex: "adapterStatus",
    key: "adapterStatus",
    render: (v: string) => <Text code>{v}</Text>,
  },
  {
    title: "Change",
    dataIndex: "changeType",
    key: "changeType",
    render: (v: AdapterChangeType) => changeTypeTag(v),
  },
  {
    title: "Old Version",
    dataIndex: "oldVersion",
    key: "oldVersion",
    render: (v: string | null) => v ?? "—",
  },
  {
    title: "New Version",
    dataIndex: "newVersion",
    key: "newVersion",
    render: (v: string | null) => v ?? "—",
  },
  {
    title: "Detail",
    dataIndex: "detail",
    key: "detail",
    render: (v: string | null) => v ?? "—",
  },
];

const AdapterResultsRow: React.FC<{ runId: number; providerResultId: number }> = ({
  runId,
  providerResultId,
}) => {
  const [adapterResults, setAdapterResults] = React.useState<CrawlerAdapterResultRecord[] | null>(
    null
  );
  const [loadingAdapters, setLoadingAdapters] = React.useState(false);
  const [changeTypeFilter, setChangeTypeFilter] = React.useState<AdapterChangeType | "ALL">("ALL");

  React.useEffect(() => {
    setLoadingAdapters(true);
    WizardService.CrawlerMonitor.adapterResults(runId, providerResultId)
      .then((results) => setAdapterResults(results))
      .catch(() => setAdapterResults([]))
      .finally(() => setLoadingAdapters(false));
  }, [runId, providerResultId]);

  const sortedAndFiltered = React.useMemo(() => {
    if (!adapterResults) return [];
    const filtered =
      changeTypeFilter === "ALL"
        ? adapterResults
        : adapterResults.filter((r) => r.changeType === changeTypeFilter);
    return [...filtered].sort(
      (a, b) => CHANGE_TYPE_ORDER[a.changeType] - CHANGE_TYPE_ORDER[b.changeType]
    );
  }, [adapterResults, changeTypeFilter]);

  return (
    <Space direction="vertical" style={{ width: "100%", paddingLeft: 48 }}>
      <Select
        value={changeTypeFilter}
        onChange={setChangeTypeFilter}
        options={CHANGE_TYPE_FILTER_OPTIONS}
        size="small"
        style={{ width: 160 }}
      />
      <Table
        columns={adapterColumns}
        dataSource={sortedAndFiltered}
        loading={loadingAdapters}
        rowKey="id"
        pagination={false}
        size="small"
      />
    </Space>
  );
};

const providerColumns: ColumnsType<CrawlerProviderResultRecord> = [
  {
    title: "Provider",
    dataIndex: "provider",
    key: "provider",
    render: (v: string) => <Text strong>{v}</Text>,
  },
  {
    title: "Platform",
    dataIndex: "platform",
    key: "platform",
    render: (v: string) => (
      <Tag color={v === "ANDROID" ? "green" : "blue"}>{v}</Tag>
    ),
  },
  {
    title: "Versions Processed",
    dataIndex: "versionsProcessed",
    key: "versionsProcessed",
    render: (v: string[]) => v.join(", ") || "—",
  },
  {
    title: "Adapters",
    dataIndex: "adaptersFound",
    key: "adaptersFound",
  },
  {
    title: "Errors",
    dataIndex: "errors",
    key: "errors",
    render: (errors: string[]) =>
      errors.length > 0 ? (
        <Tooltip title={errors.join("\n")}>
          <Tag color="red">{errors.length}</Tag>
        </Tooltip>
      ) : (
        <Tag color="green">0</Tag>
      ),
  },
  {
    // The SPM crawl is folded into the iOS pass only, so Android rows are legitimately 0/0/0.
    // Rendered as a single column so the three counters stay visually bound together — read
    // apart they invite the wrong conclusion (e.g. "12 skipped" as a failure count).
    title: (
      <Tooltip
        title={
          "SPM upserted / skipped / collapsed. iOS pass only — Android rows are always 0. " +
          "`skipped` lumps together a decoder failure, an ordinary no-matching-adapter/SDK-row " +
          "miss, and a throwing source, so it is not itself an error count — see " +
          "GET /v4/spm/outcomes for the per-tag breakdown."
        }
      >
        <Space size={4}>
          SPM
          <QuestionCircleOutlined />
        </Space>
      </Tooltip>
    ),
    key: "spm",
    render: (_: unknown, record: CrawlerProviderResultRecord) => {
      const upserted = record.spmUpserted ?? 0;
      const skipped = record.spmSkipped ?? 0;
      const collapsed = record.spmCollapsed ?? 0;

      if (upserted === 0 && skipped === 0 && collapsed === 0) {
        // On Android the SPM pass legitimately never runs, so `—` (not applicable) is honest.
        // On iOS the same 0/0/0 is a real outcome and must not be dressed up as "n/a": it is
        // what a crawl that threw before its first counter increment records
        // (`SpmCrawlResult.EMPTY`), and it is also what "no SPM sources configured" produces.
        // The two are indistinguishable from here, so say zero rather than hide it.
        if (record.platform !== "IOS") {
          return <Text type="secondary">—</Text>;
        }
        return (
          <Tooltip title="iOS pass produced no SPM rows at all. Either no SPM sources are configured for this provider, or the crawl threw before its first upsert. This run record alone still can't tell those apart — GET /v4/spm/outcomes reports per-tag outcomes by provider, but its rows aren't tied to a specific run yet, so it can show whether this provider's crawls are throwing in general, not confirm the cause of this particular run.">
            <Tag color="orange">0 / 0 / 0</Tag>
          </Tooltip>
        );
      }

      return (
        <Space size={4}>
          <Tooltip title="Upserted">
            <Tag color="green">{upserted}</Tag>
          </Tooltip>
          <Tooltip title="Skipped — breakdown at GET /v4/spm/outcomes">
            <Tag color="orange">{skipped}</Tag>
          </Tooltip>
          <Tooltip title="Collapsed into an existing (target, product)">
            <Tag>{collapsed}</Tag>
          </Tooltip>
        </Space>
      );
    },
  },
  {
    title: "Duration",
    dataIndex: "durationMs",
    key: "durationMs",
    render: (v: number) => formatDuration(v),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status: string, record: CrawlerProviderResultRecord) =>
      statusTag(status, record.adaptersSupported),
  },
];

const ExpandedRow: React.FC<{ runId: number }> = ({ runId }) => {
  const [providerResults, setProviderResults] = React.useState<
    CrawlerProviderResultRecord[] | null
  >(null);
  const [loadingProviders, setLoadingProviders] = React.useState(false);
  const [expandedProviderKeys, setExpandedProviderKeys] = React.useState<number[]>([]);

  React.useEffect(() => {
    setLoadingProviders(true);
    WizardService.CrawlerMonitor.runDetail(runId)
      .then((detail) => setProviderResults(detail.results))
      .catch(() => setProviderResults([]))
      .finally(() => setLoadingProviders(false));
  }, [runId]);

  return (
    <Table
      columns={providerColumns}
      dataSource={providerResults ?? []}
      loading={loadingProviders}
      rowKey="id"
      pagination={false}
      size="small"
      expandable={{
        expandedRowKeys: expandedProviderKeys,
        onExpand: (expanded, record) => {
          setExpandedProviderKeys(
            expanded
              ? [...expandedProviderKeys, record.id]
              : expandedProviderKeys.filter((k) => k !== record.id)
          );
        },
        expandedRowRender: (record) => (
          <AdapterResultsRow runId={runId} providerResultId={record.id} />
        ),
      }}
    />
  );
};

export const RunHistoryTable: React.FC<RunHistoryTableProps> = ({
  history,
  loading,
  onPageChange,
  currentPage,
  pageSize,
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  const columns: ColumnsType<CrawlerRunRecord> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: "Started At",
      dataIndex: "startedAt",
      key: "startedAt",
      render: (v: string) => formatDateTime(v),
    },
    {
      title: "Duration",
      dataIndex: "totalDurationMs",
      key: "totalDurationMs",
      render: (v: number | null) => formatDuration(v),
    },
    {
      title: "Triggered By",
      dataIndex: "triggeredBy",
      key: "triggeredBy",
      render: (v: string) => (
        <Tag color={v === "manual" ? "blue" : "default"}>{v}</Tag>
      ),
    },
    {
      title: "Adapters",
      dataIndex: "totalAdaptersFound",
      key: "totalAdaptersFound",
    },
    {
      title: "Errors",
      dataIndex: "totalErrors",
      key: "totalErrors",
      render: (v: number) =>
        v > 0 ? <Tag color="red">{v}</Tag> : <Tag color="green">0</Tag>,
    },
    {
      title: "Status",
      dataIndex: "success",
      key: "status",
      render: (success: boolean) =>
        success ? (
          <Tag color="green">success</Tag>
        ) : (
          <Tag color="red">failed</Tag>
        ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={history?.items ?? []}
      loading={loading}
      rowKey="id"
      expandable={{
        expandedRowKeys,
        onExpand: (expanded, record) => {
          setExpandedRowKeys(
            expanded
              ? [...expandedRowKeys, record.id]
              : expandedRowKeys.filter((k) => k !== record.id)
          );
        },
        expandedRowRender: (record) => <ExpandedRow runId={record.id} />,
      }}
      pagination={{
        current: currentPage,
        pageSize,
        total: history?.total ?? 0,
        onChange: onPageChange,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
        showTotal: (total) => `${total} runs`,
      }}
    />
  );
};
